import { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/auth';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useMessages } from '../../contexts/messages';
import EmailEditor from 'react-email-editor';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { toast } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { EMPTY_EDITOR_DESIGN } from '@Constants/html-templates';
import { useMessageTemplate } from '../../hooks/useMessageTemplate';
import {
  useQuickMessageCategories,
  useQuickMessageMergeTags,
} from '../../hooks/useQuickMessages';
import AiSnippetPickerModal from '../AiSnippetPickerModal';

const DEFAULT_METHOD_OPTIONS = [
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'EMAIL' },
  { value: 'PUSH', label: 'PUSH' },
];

/** The inbox sends SMS, email, and in-app messages; push is campaign-only. */
const QUICK_MESSAGE_METHOD_OPTIONS = DEFAULT_METHOD_OPTIONS.filter(
  (option) => option.value !== 'PUSH',
);

const MessageTemplateForm = ({
  onSubmitHandler,
  defaultValues = {},
  isEdit = false,
}) => {
  const [editorMergeTags, setEditorMergeTags] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [oldTitle, setOldTitle] = useState('');
  const [templateArray, setTemplateArray] = useState([]);
  const [activeField, setActiveField] = useState([]);
  const [aiSnippetModalOpen, setAiSnippetModalOpen] = useState(false);
  const emailEditorRef = useRef(null);

  const { user } = useAuth();

  const {
    fetchMergeTags,
    mergeTags,
    showHTMLEditor,
    setShowHTMLEditor,
    isHtmlDesignBlank,
  } = useMessages();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      method: defaultValues.method || 'EMAIL',
      name: defaultValues.name || '',
      messageSubject: defaultValues.messageSubject || '',
      messageBody: defaultValues.messageBody || '',
      html: defaultValues.html || null,
      htmlEditorJson: defaultValues.htmlEditorJson ?? EMPTY_EDITOR_DESIGN,
      createdBy: defaultValues.createdBy || null,
      isQuickMessage: defaultValues.isQuickMessage ?? false,
      category: defaultValues.category ?? '',
    },
  });

  const title = watch('name');
  const method = watch('method');
  const hasName = watch('name')?.length > 0;
  const hasMessageSubject = watch('messageSubject')?.length > 0;
  const isQuickMessage = watch('isQuickMessage');
  const methodOptions = isQuickMessage
    ? QUICK_MESSAGE_METHOD_OPTIONS
    : DEFAULT_METHOD_OPTIONS;

  const { data: categories = [], isLoading: categoriesLoading } =
    useQuickMessageCategories({ enabled: isQuickMessage });
  const { data: inboxMergeTags = [] } = useQuickMessageMergeTags({
    enabled: isQuickMessage,
  });

  const customStyles = {
    control: (styles) => ({
      ...styles,
      borderRadius: '8px',
    }),
  };

  // Inbox sends only have the recipient fish to read tags from, so quick
  // messages offer fish and subscription tags only.
  const mergeTagOptions = (isQuickMessage ? inboxMergeTags : mergeTags).map((tag) => ({
    value: `[[${tag.id}]]`,
    label: tag.label,
  }));

  const { data: templatePayload } = useMessageTemplate({
    searchParams: {
      '[filter][method]': method,
    },
  });

  useEffect(() => {
    if (templatePayload?.data) {
      setTemplateArray(templatePayload.data);
    }
  }, [templatePayload]);

  const messageTemplateOptions = templateArray?.map((template) => {
    let designJson = EMPTY_EDITOR_DESIGN;
    if (
      template.htmlEditorJson &&
      Object.keys(template.htmlEditorJson).length > 0
    ) {
      designJson = template.htmlEditorJson;
    }
    return {
      id: template.id,
      value: template.messageBody,
      subject: template.messageSubject,
      html: template.html ?? '',
      htmlEditorJson: designJson,
      method: template.method,
      label: template.name,
    };
  });

  const handleTemplateChange = (selected) => {
    if (!selected) return;

    setValue('messageBody', selected.value);
    setValue('messageSubject', selected.subject);
    setValue('html', selected.html || null);
    setValue('htmlEditorJson', selected.htmlEditorJson);

    const unlayer = emailEditorRef.current?.editor;
    if (!unlayer) return;
    unlayer.loadDesign(selected.htmlEditorJson);
  };

  const handleMergeTagChange = (selected) => {
    if (!selected) return;

    if (activeField === 'body') {
      const messageBody = watch('messageBody') || '';
      const textArea = document.querySelector('textarea[name="messageBody"]');

      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;

      const newValue =
        messageBody.substring(0, start) +
        selected.value +
        messageBody.substring(end);

      setValue('messageBody', newValue);

      setTimeout(() => {
        textArea.focus();
        textArea.setSelectionRange(
          start + selected.value.length,
          start + selected.value.length,
        );
      }, 0);
    } else if (activeField === 'subject') {
      const messageSubject = watch('messageSubject') || '';
      const input = document.querySelector('input[name="messageSubject"]');
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const newValue =
        messageSubject.substring(0, start) +
        selected.value +
        messageSubject.substring(end);

      setValue('messageSubject', newValue);

      setTimeout(() => {
        input.focus();
        input.setSelectionRange(
          start + selected.value.length,
          start + selected.value.length,
        );
      }, 0);
    } else {
      const messageBody = watch('messageBody') || '';
      setValue('messageBody', messageBody + selected.value);
    }
  };

  const exportEmail = async () => {
    const unlayer = emailEditorRef.current?.editor;
    if (!unlayer) return;

    const data = await new Promise((resolve) => {
      unlayer.exportHtml((d) => resolve(d));
    });

    if (isHtmlDesignBlank(data.design)) {
      setValue('html', null);
      setValue('htmlEditorJson', null);

      return { html: null, design: null };
    } else {
      setValue('html', data.html);
      setValue('htmlEditorJson', data.design);

      return { html: data.html, design: data.design };
    }
  };

  const onReady = (unlayer) => {
    const design = getValues('htmlEditorJson');
    if (design) {
      unlayer.loadDesign(design);
    }
  };

  const onSubmit = async (data) => {
    if (data.isQuickMessage) {
      data.html = null;
      data.htmlEditorJson = null;
    } else if (data.method === 'EMAIL') {
      const htmlObj = await exportEmail();
      data.html = htmlObj?.html;
      data.htmlEditorJson = htmlObj?.design;
    }

    await onSubmitHandler(data).then(async (res) => {
      setOldTitle(title);
      if (res.ok) {
        toast(`Save message template - successful!`);
        reset();
        setShowHTMLEditor(false);
        setErrorMessage('');
      } else {
        const resData = await res.json();
        toast(resData.message);
        setErrorMessage(resData.message);
      }
    });
  };

  useEffect(() => {
    if (user) {
      setValue('createdBy', user.id);
    }
  }, [user]);

  useEffect(() => {
    fetchMergeTags();

    if (!mergeTagOptions.length) return;
    const editorTagObj = {};

    mergeTagOptions.forEach((mergeTag) => {
      const name = mergeTag.label.replaceAll(' ', '_').toLowerCase();

      editorTagObj[name] = {
        name: mergeTag.label,
        value: `{{${mergeTag.value.slice(2, -2)}}}`,
        sample: 'Answer',
      };
    });

    setEditorMergeTags(editorTagObj);
  }, []);

  useEffect(() => {
    if (method === 'EMAIL' && !getValues('isQuickMessage')) {
      if (isEdit) {
        if (getValues('html')) {
          setShowHTMLEditor(true);
        }
      } else {
        setShowHTMLEditor(true);
      }
    }
  }, [method]);

  return (
    <div className="">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700">
              Template Title
            </label>
            <Controller
              name="name"
              control={control}
              rules={{ required: `A template title is required` }}
              render={({ field }) => (
                <input
                  type="text"
                  {...field}
                  className={`mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 shadow-sm ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                />
              )}
            />

            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700">
              Communication Channel
            </label>
            <Controller
              name="method"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={
                    methodOptions.find(
                      (option) => option.value === value,
                    ) || null
                  }
                  onChange={(selected) => {
                    setShowHTMLEditor(
                      selected.value === 'EMAIL' && !isQuickMessage,
                    );
                    return onChange(selected?.value || '');
                  }}
                  options={methodOptions}
                  placeholder="Select method..."
                  classNamePrefix="select"
                  styles={customStyles}
                  noOptionsMessage={() => 'No methods found'}
                  className={errors.method ? 'border-red-500' : ''}
                />
              )}
            />
          </div>

          <div className="mb-5">
            <Controller
              name="isQuickMessage"
              control={control}
              render={({ field: { value, onChange } }) => (
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onChange(checked);
                      if (checked) {
                        setShowHTMLEditor(false);
                        if (getValues('method') === 'PUSH') {
                          setValue('method', 'SMS');
                        }
                      }
                    }}
                  />
                  Show in inbox Quick Messages
                </label>
              )}
            />
            <p className="mt-1 text-xs text-gray-500">
              Staff can insert this plain-text message from the inbox composer.
              It's hidden from campaign and flow template lists.
            </p>
          </div>

          {isQuickMessage && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <Controller
                name="category"
                control={control}
                rules={{
                  maxLength: {
                    value: 50,
                    message: 'Category must be 50 characters or fewer',
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <CreatableSelect
                    value={value ? { value, label: value } : null}
                    onChange={(selected) => onChange(selected?.value ?? '')}
                    options={categories.map((category) => ({
                      value: category,
                      label: category,
                    }))}
                    isClearable
                    isLoading={categoriesLoading}
                    formatCreateLabel={(input) => `Create "${input.trim()}"`}
                    placeholder="Search or create a category..."
                    classNamePrefix="select"
                    styles={customStyles}
                    className="mt-1"
                  />
                )}
              />
              {errors.category && (
                <p className="text-sm text-red-500">{errors.category.message}</p>
              )}
            </div>
          )}
        </div>

        {method === 'EMAIL' && !isQuickMessage && (
          <div className="mt-4 mb-2 flex items-center gap-4">
            <div className="flex w-fit justify-center">
              <span
                className={
                  showHTMLEditor === false
                    ? 'font-bold text-[#CAAB6A]'
                    : 'text-gray-400'
                }
              >
                Message
              </span>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={showHTMLEditor === true}
                onChange={async () => {
                  if (showHTMLEditor) {
                    await exportEmail();
                  }
                  setShowHTMLEditor(!showHTMLEditor);
                }}
              />
              <div className="peer h-6 w-11 rounded-full bg-gray-300 peer-focus:outline-none after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
            </label>
            <div className="flex w-fit justify-center">
              <span
                className={
                  showHTMLEditor === true
                    ? 'font-bold text-[#CAAB6A]'
                    : 'text-gray-400'
                }
              >
                HTML
              </span>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between">
            {method !== 'SMS' && (
              <label className="block text-sm font-medium text-gray-700">
                {method === 'EMAIL' ? 'Message Subject' : 'Push Title'}
              </label>
            )}
            <div className="flex gap-2">
              <Select
                options={mergeTagOptions}
                onChange={handleMergeTagChange}
                placeholder="Insert Merge Tag"
                isClearable
                className="w-48"
                classNamePrefix="select"
                value={null}
              />

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAiSnippetModalOpen(true)}
              >
                Insert AI Snippet
              </Button>

              <AiSnippetPickerModal
                open={aiSnippetModalOpen}
                onOpenChange={setAiSnippetModalOpen}
                onInsert={(tag) => handleMergeTagChange({ value: tag })}
              />

              <Select
                options={messageTemplateOptions}
                onChange={handleTemplateChange}
                placeholder="Templates"
                isClearable
                className="w-48"
                classNamePrefix="select"
                value={null}
              />
            </div>
          </div>
          {method !== 'SMS' && (
            <>
              <Controller
                name="messageSubject"
                control={control}
                rules={{
                  required: `A ${method === 'EMAIL' ? 'message subject' : 'push title'} is required`,
                }}
                render={({ field }) => (
                  <input
                    onFocus={() => setActiveField('subject')}
                    type="text"
                    {...field}
                    className={`mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 shadow-sm ${
                      errors.messageSubject ? 'border-red-500' : ''
                    }`}
                  />
                )}
              />

              {errors.messageSubject && (
                <p className="text-sm text-red-500">
                  {errors.messageSubject.message}
                </p>
              )}
            </>
          )}
        </div>

        <label className="block text-sm font-medium text-gray-700">
          Message Body
        </label>

        <div className={`border-1 ${showHTMLEditor && !isQuickMessage ? 'block' : 'hidden'}`}>
          <EmailEditor
            key={JSON.stringify(Object.keys(editorMergeTags))}
            ref={emailEditorRef}
            onReady={onReady}
            options={{
              displayMode: 'email',
              appearance: {
                panels: {
                  tools: {
                    dock: 'left',
                  },
                },
              },
              mergeTags: editorMergeTags,
            }}
          />
        </div>

        {(!showHTMLEditor || isQuickMessage) && (
          <div>
            <Controller
              name="messageBody"
              control={control}
              rules={{ required: 'Message body is required' }}
              render={({ field }) => (
                <textarea
                  onFocus={() => setActiveField('body')}
                  rows="5"
                  {...field}
                  className={`mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 shadow-sm ${
                    errors.messageBody ? 'border-red-500' : ''
                  }`}
                />
              )}
            />

            {errors.messageBody && (
              <p className="text-sm text-red-500">
                {errors.messageBody.message}
              </p>
            )}
            {isQuickMessage && (
              <p className="mt-1 text-xs text-gray-500">
                Merge tags fill in from the recipient fish when the message is
                sent, so only fish and subscription tags are offered. An SMS
                with a tag the fish has no value for is not sent.
              </p>
            )}
          </div>
        )}

        {/* Form-level validation messages - only show after submission attempt */}
        {isSubmitted && !hasName && (
          <p className="text-center text-sm text-red-500">
            A template title is required
          </p>
        )}
        {isSubmitted && method !== 'SMS' && !hasMessageSubject && (
          <p className="text-center text-sm text-red-500">
            A {method === 'EMAIL' ? 'message subject' : 'push title'} is
            required
          </p>
        )}
        {isSubmitted && errorMessage && oldTitle === title && (
          <p className="text-center text-sm text-red-500">{errorMessage}</p>
        )}

        <Button
          type="submit"
          className=""
          disabled={
            (isSubmitted && !hasName) ||
            (isSubmitted && method !== 'SMS' && !hasMessageSubject)
          }
        >
          Submit
        </Button>
      </form>
    </div>
  );
};

export default MessageTemplateForm;
